import Alert from "@cloudscape-design/components/alert"
import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import ExpandableSection from "@cloudscape-design/components/expandable-section"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import Textarea from "@cloudscape-design/components/textarea"
import React, { useState } from "react"
import { useProposals, useReviewProposal } from "@/hooks/queries/useCollaboration"
import { isForbidden } from "@/utils/apiErrors"
import DiffView from "./DiffView"
import { formatDateTime } from "./helpers"

interface ProposalsSectionProps {
  dtaId: string
  isDAE?: boolean
  onProposalUpdated?: () => void
  version: string
}

const ProposalsSection: React.FC<ProposalsSectionProps> = ({
  dtaId,
  isDAE = false,
  onProposalUpdated,
  version,
}) => {
  const { data, isPending, error: fetchError } = useProposals(dtaId, version)
  const reviewProposal = useReviewProposal(dtaId)
  const [reviewComment, setReviewComment] = useState("")
  const [showReviewForm, setShowReviewForm] = useState<string | null>(null)
  const [expandedProposalId, setExpandedProposalId] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  /*
   * Treat a 403 on the read as "nothing to show": render the normal empty state rather than the raw
   * error payload, so the user never sees `Forbidden - {"error":"Forbidden"}`.
   */
  const error = localError || (fetchError && !isForbidden(fetchError) ? fetchError.message : null)
  const proposals = data?.proposals ?? []

  const handleReview = async (proposalId: string, status: "ACCEPTED" | "REJECTED") => {
    if (!reviewComment.trim() && status === "REJECTED") {
      setLocalError("Please provide a reason for rejection")
      return
    }

    setLocalError(null)
    try {
      await reviewProposal.mutateAsync({
        proposalId,
        status,
        reviewComment: reviewComment.trim() || undefined,
      })
      setShowReviewForm(null)
      setReviewComment("")
      onProposalUpdated?.()
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Failed to update proposal")
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Badge color="blue">Pending</Badge>
      case "ACCEPTED":
        return <Badge color="green">Accepted</Badge>
      case "REJECTED":
        return <Badge color="red">Rejected</Badge>
      default:
        return <Badge color="grey">{status}</Badge>
    }
  }

  const formatValue = (value: unknown): string => {
    if (typeof value === "string") return value
    if (value === null || value === undefined) return "N/A"
    return JSON.stringify(value, null, 2)
  }

  const sortedProposals = [...proposals].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )

  if (isPending) {
    return (
      <Container variant="stacked">
        <Box textAlign="center" padding={{ vertical: "xl" }}>
          <Spinner />{" "}
          <Box display="inline" color="text-body-secondary">
            Loading proposals...
          </Box>
        </Box>
      </Container>
    )
  }

  return (
    <Container
      variant="stacked"
      header={<Header counter={`(${proposals.length})`}>Proposals</Header>}
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error}</Alert>}

        {sortedProposals.length === 0 ? (
          <Box textAlign="center" padding={{ vertical: "xl" }} color="text-body-secondary">
            No proposals yet.
          </Box>
        ) : (
          <SpaceBetween size="m">
            {sortedProposals.map((proposal) => (
              <Container
                key={proposal.proposal_id}
                variant="stacked"
                header={
                  <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                    <Box>
                      <SpaceBetween size="xxs">
                        <Box>
                          <Box variant="strong" display="inline">
                            {proposal.user_id}
                          </Box>{" "}
                          <Box display="inline" color="text-body-secondary">
                            · {proposal.section_reference}
                          </Box>
                        </Box>
                        <Box color="text-body-secondary" fontSize="body-s">
                          {formatDateTime(proposal.created_at)}
                        </Box>
                      </SpaceBetween>
                    </Box>
                    {getStatusBadge(proposal.status)}
                  </SpaceBetween>
                }
              >
                <SpaceBetween size="s">
                  <Box>{proposal.justification}</Box>

                  <ExpandableSection
                    variant="footer"
                    headerText={
                      expandedProposalId === proposal.proposal_id
                        ? "Hide Comparison"
                        : "Show Comparison"
                    }
                    expanded={expandedProposalId === proposal.proposal_id}
                    onChange={({ detail }) =>
                      setExpandedProposalId(detail.expanded ? proposal.proposal_id : null)
                    }
                  >
                    <SpaceBetween size="xs">
                      <Box fontSize="body-s" fontWeight="bold" color="text-body-secondary">
                        Changes:
                      </Box>
                      <DiffView
                        original={formatValue(proposal.original_value)}
                        proposed={formatValue(proposal.proposed_value)}
                      />
                    </SpaceBetween>
                  </ExpandableSection>

                  {proposal.reviewed_at && (
                    <Box>
                      <SpaceBetween size="xxs">
                        <Box color="text-body-secondary" fontSize="body-s">
                          {proposal.status} by {proposal.reviewed_by} ·{" "}
                          {formatDateTime(proposal.reviewed_at)}
                        </Box>
                        {proposal.review_comment && (
                          <Box variant="small">
                            <em>{proposal.review_comment}</em>
                          </Box>
                        )}
                      </SpaceBetween>
                    </Box>
                  )}

                  {isDAE && proposal.status === "PENDING" && (
                    <Box>
                      {showReviewForm === proposal.proposal_id ? (
                        <SpaceBetween size="s">
                          <Textarea
                            value={reviewComment}
                            onChange={({ detail }) => setReviewComment(detail.value)}
                            placeholder="Comment (required for rejection)..."
                            rows={2}
                          />
                          <SpaceBetween size="xs" direction="horizontal">
                            <Button
                              variant="primary"
                              onClick={() => void handleReview(proposal.proposal_id, "ACCEPTED")}
                              disabled={reviewProposal.isPending}
                            >
                              Accept
                            </Button>
                            <Button
                              onClick={() => void handleReview(proposal.proposal_id, "REJECTED")}
                              disabled={reviewProposal.isPending || !reviewComment.trim()}
                            >
                              Reject
                            </Button>
                            <Button
                              variant="link"
                              onClick={() => {
                                setShowReviewForm(null)
                                setReviewComment("")
                              }}
                            >
                              Cancel
                            </Button>
                          </SpaceBetween>
                        </SpaceBetween>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={() => setShowReviewForm(proposal.proposal_id)}
                        >
                          Review
                        </Button>
                      )}
                    </Box>
                  )}
                </SpaceBetween>
              </Container>
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  )
}

export default ProposalsSection
